import React from "react";
import { Loan } from "../types";

interface LoanPerformanceTableProps {
  loans: Loan[];
  activeFilter: string;
  onFilterChange: (filter: string) => void;
}

export const LoanPerformanceTable: React.FC<LoanPerformanceTableProps> = ({
  loans,
  activeFilter,
  onFilterChange,
}) => {
  const statusFilters = ["All", "Current", "30+ DPD", "90+ DPD"];
  const cityFilters = ["Toronto", "Vancouver", "Calgary"];

  return (
    <article className="relative p-4 pb-4.5 rounded-[18px] bg-gradient-to-br from-[#22284d] to-[#10142f] border border-[rgba(139,146,176,0.2)] shadow-[0_16px_40px_rgba(0,0,0,0.6)] overflow-hidden">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex flex-col gap-0.5">
          <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
            Loan performance · sample view
          </div>
          <div className="text-[11px] text-[#8b92b0]">
            Filterable, export-ready dataset of anonymized loan-level
            performance.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2.5 mb-2 text-xs text-[#8b92b0] flex-wrap">
        <div className="inline-flex items-center gap-1.5 flex-wrap">
          {statusFilters.map((filter) => (
            <span
              key={filter}
              onClick={() => onFilterChange(filter)}
              className={`px-2.25 py-1 rounded-full border cursor-pointer text-[11px] transition-all duration-200 ${
                activeFilter === filter
                  ? "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]"
                  : "border-[rgba(139,146,176,0.7)] bg-[rgba(5,7,22,0.9)] text-[#8b92b0]"
              }`}
            >
              {filter}
            </span>
          ))}
        </div>
        <div className="inline-flex items-center gap-1.5 flex-wrap">
          {cityFilters.map((city) => (
            <span
              key={city}
              className="px-2.25 py-1 rounded-full border border-[rgba(139,146,176,0.7)] bg-[rgba(5,7,22,0.9)] text-[11px] text-[#8b92b0] cursor-pointer"
            >
              {city}
            </span>
          ))}
        </div>
      </div>

      <div className="rounded-xl overflow-auto border border-[rgba(139,146,176,0.35)] bg-gradient-to-b from-[#1c2243] to-[#050716] max-h-[360px]">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 z-[1] bg-[rgba(5,7,22,0.95)] backdrop-blur-sm">
            <tr>
              {[
                "Loan ID",
                "Amount",
                "LTV",
                "Rate",
                "Location",
                "Status",
                "DPD",
              ].map((header) => (
                <th
                  key={header}
                  className="px-2.5 py-2.25 text-left border-b border-[rgba(139,146,176,0.14)] text-[11px] uppercase tracking-[0.16em] text-[#8b92b0] whitespace-nowrap"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loans.map((loan) => (
              <tr
                key={loan.id}
                className="hover:bg-[rgba(0,212,255,0.08)] transition-colors"
              >
                <td className="px-2.5 py-2.25 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  #{loan.id}
                </td>
                <td className="px-2.5 py-2.25 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {loan.amount}
                </td>
                <td className="px-2.5 py-2.25 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {loan.ltv}
                </td>
                <td className="px-2.5 py-2.25 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {loan.rate}
                </td>
                <td className="px-2.5 py-2.25 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {loan.location}
                </td>
                <td className="px-2.5 py-2.25 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.75 rounded-full text-[10px] uppercase tracking-[0.12em] ${
                      loan.status === "current"
                        ? "bg-[rgba(0,255,136,0.18)] text-neon-green border border-[rgba(0,255,136,0.7)]"
                        : loan.status === "late"
                        ? "bg-[rgba(255,184,0,0.18)] text-warning-amber border border-[rgba(255,184,0,0.7)]"
                        : "bg-[rgba(255,51,102,0.18)] text-error-red border border-[rgba(255,51,102,0.7)]"
                    }`}
                  >
                    {loan.status === "current"
                      ? "Current"
                      : loan.status === "late"
                      ? "30 Days"
                      : "90 Days"}
                  </span>
                </td>
                <td className="px-2.5 py-2.25 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {loan.dpd}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-1.5 text-[11px] text-[#8b92b0] flex items-center justify-between gap-2 flex-wrap">
        <span>
          Showing <strong>{loans.length}</strong> of <strong>10,847</strong>{" "}
          active loans
        </span>
        <span>Use API for full export · v1.3 schema</span>
      </div>
    </article>
  );
};
